import { useState } from "react";
import { motion } from "motion/react";
import {
  ArrowRight,
  MapPin,
  Briefcase,
  Clock,
  Users,
  Star,
  CheckCircle2,
  Building2,
  Heart,
  Sparkles,
} from "lucide-react";
import { Navbar } from "../../Components/Navbar";
import { CareerNav } from "../../Data/navLinks";
import ApplicationForm from "../../Components/Career/ApplicationForm";
import logo from "../../assets/logo/logo.jpeg"

// ── Data ───────────────────────────────────────────────────────────────────────
const WHY_JOIN = [
  {
    emoji: "💰",
    title: "High Base Salary + Commission + Allowances",
    desc: "Your income potential is unlimited. We reward ambition — the harder you work, the more you earn.",
    color: "from-violet-600 to-purple-600",
    shadow: "shadow-violet-500/25",
    light: "bg-violet-50",
    text: "text-violet-700",
  },
  {
    emoji: "🎓",
    title: "Comprehensive Training + Mentorship",
    desc: "No experience? No problem. We'll guide you every step with structured onboarding and a personal mentor.",
    color: "from-orange-500 to-amber-500",
    shadow: "shadow-orange-500/25",
    light: "bg-orange-50",
    text: "text-orange-700",
  },
  {
    emoji: "⚡",
    title: "Young, Energetic Team",
    desc: "Fast-paced, supportive, and fun — like family. We work hard together and celebrate together.",
    color: "from-pink-500 to-rose-500",
    shadow: "shadow-pink-500/25",
    light: "bg-pink-50",
    text: "text-pink-700",
  },
  {
    emoji: "📈",
    title: "Clear Career Growth Path",
    desc: "Intern → Full-Time → Team Leader. Your future starts here. We promote from within, always.",
    color: "from-cyan-500 to-blue-500",
    shadow: "shadow-cyan-500/25",
    light: "bg-cyan-50",
    text: "text-cyan-700",
  },
  {
    emoji: "✈️",
    title: "Travel Opportunities",
    desc: "Attend university fairs, education expos, and partner visits abroad. Broaden your world while doing meaningful work.",
    color: "from-lime-500 to-green-500",
    shadow: "shadow-lime-500/25",
    light: "bg-lime-50",
    text: "text-lime-700",
  },
];

const PERKS = [
  { icon: <Users className="w-4 h-4" />, label: "Flexible career tracks for fresh grads & experienced professionals" },
  { icon: <Star className="w-4 h-4" />, label: "Monthly team bonding activities and performance incentives" },
  { icon: <Sparkles className="w-4 h-4" />, label: "Recognition programs for top performers" },
  { icon: <Building2 className="w-4 h-4" />, label: "Office environment designed for collaboration and creativity" },
  { icon: <Heart className="w-4 h-4" />, label: "Transparent leadership and open communication culture" },
];

const OPEN_ROLES = [
  {
    title: "Sales Education Consultant",
    type: "Full Time",
    location: "Shah Alam, Selangor",
    dept: "Sales",
    tags: ["Sales", "Education", "Selangor"],
    color: "violet",
  },
  {
    title: "Sales Education Consultant",
    type: "Full Time",
    location: "Kuantan, Pahang",
    dept: "Sales",
    tags: ["Sales", "Education", "Pahang"],
    color: "orange",
  },
];

const OFFICES = [
  {
    name: "SCS Education Centre",
    city: "Kuantan",
    state: "Pahang",
    address: "No. B6, 1st Floor, Lorong Seri Kuantan 80, Jalan Beserah, Star City 1, 25300 Kuantan, Pahang",
    emoji: "🏢",
    color: "from-violet-500 to-purple-500",
  },
  {
    name: "SCS Education Centre",
    city: "Shah Alam",
    state: "Selangor",
    address: "A-5-2, 179, Jalan Zuhal U5/178, Seksyen U5, 40150 Shah Alam, Selangor",
    emoji: "🏙️",
    color: "from-orange-500 to-pink-500",
  },
  {
    name: "SCS Education",
    city: "Kepong",
    state: "Kuala Lumpur",
    address: "32A-2, Jalan Rimbunan Raya, Laman Rimbunan, 52100 Kepong, Kuala Lumpur",
    emoji: "🌆",
    color: "from-cyan-500 to-blue-500",
  },
];

// ── Main Page ──────────────────────────────────────────────────────────────────
export default function CareerPage() {
  const [modalOpen, setModalOpen] = useState(false);
  const [prefilledPosition, setPrefilledPosition] = useState("");
  const [prefilledLocation, setPrefilledLocation] = useState("");

  const openModal = (position = "", location = "") => {
    setPrefilledPosition(position);
    setPrefilledLocation(location);
    setModalOpen(true);
  };

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: "Inter, sans-serif" }}>
      <Navbar links={CareerNav} />
      <ApplicationForm
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        prefilledPosition={prefilledPosition}
        prefilledLocation={prefilledLocation}
      />

      {/* ════════════════════════════════════════════════════════════════
          HERO
      ════════════════════════════════════════════════════════════════ */}
      <section className="relative min-h-screen bg-white overflow-hidden flex items-center pt-16">
        {/* Dot grid */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            backgroundImage: "radial-gradient(circle, #e2e8f0 1.5px, transparent 1.5px)",
            backgroundSize: "28px 28px",
            maskImage: "radial-gradient(ellipse 90% 70% at 50% 30%, black 30%, transparent 100%)",
            opacity: 0.7,
          }}
        />
        {/* Blobs */}
        <div className="absolute -top-48 -right-48 w-[700px] h-[700px] rounded-full bg-violet-400/8 blur-3xl pointer-events-none" />
        <div className="absolute top-1/2 -left-48 w-[500px] h-[500px] rounded-full bg-orange-300/8 blur-3xl pointer-events-none" />

        <div className="max-w-7xl mx-auto px-5 sm:px-8 w-full py-16 lg:py-0">
          <div className="grid lg:grid-cols-[1fr_1fr] gap-10 xl:gap-16 items-center">
            {/* Left */}
            <div className="relative z-10 max-w-xl">
              <motion.div
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-violet-50 border border-violet-100 mb-7"
              >
                <span className="w-2 h-2 rounded-full bg-violet-500 animate-pulse" />
                <span className="text-[12px] text-violet-700" style={{ fontWeight: 600 }}>
                  Now Hiring — 2 Open Positions
                </span>
              </motion.div>

              <motion.h1
                initial={{ opacity: 0, y: 28 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.65, delay: 0.08 }}
                className="text-[44px] sm:text-[54px] lg:text-[64px] text-slate-900 tracking-[-2.5px] leading-[1.01] mb-6"
                style={{ fontWeight: 900 }}
              >
                Build Your{" "}
                <span
                  style={{
                    background: "linear-gradient(135deg, #7C3AED 0%, #EC4899 50%, #F97316 100%)",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    backgroundClip: "text",
                  }}
                >
                  Career.
                </span>
                <br />
                Change Lives.
              </motion.h1>

              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.18 }}
                className="text-[17px] text-slate-500 mb-9 leading-[1.75] max-w-[490px]"
                style={{ fontWeight: 400 }}
              >
                Join{" "}
                <span className="text-violet-600" style={{ fontWeight: 600 }}>SCS Education</span> — one of Malaysia's fastest-growing education consultancies. We've been helping students find their future since{" "}
                <span className="text-slate-800" style={{ fontWeight: 600 }}>2016</span>, and we need ambitious people to grow with us.
              </motion.p>

              <motion.div
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.28 }}
                className="flex flex-col sm:flex-row gap-3"
              >
                <button
                  onClick={() => openModal()}
                  className="inline-flex items-center justify-center gap-2 px-7 py-4 rounded-2xl bg-gradient-to-r from-violet-600 via-purple-600 to-violet-700 text-white text-[15px] shadow-xl shadow-violet-500/30 hover:shadow-violet-500/50 hover:-translate-y-0.5 transition-all cursor-pointer"
                  style={{ fontWeight: 700 }}
                >
                  Apply Now <ArrowRight className="w-4 h-4" />
                </button>
                <a
                  href="#roles"
                  className="inline-flex items-center justify-center gap-2 px-7 py-4 rounded-2xl border border-slate-200 text-slate-700 text-[15px] hover:bg-slate-50 hover:border-slate-300 transition-all"
                  style={{ fontWeight: 600 }}
                >
                  View Open Roles
                </a>
              </motion.div>

              {/* Quick stats */}
              <motion.div
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.4 }}
                className="grid grid-cols-3 gap-4 mt-10 pt-8 border-t border-slate-100"
              >
                {[
                  { val: "4000+", label: "Students Enrolled" },
                  { val: "300+", label: "Partner universities" },
                  { val: "3", label: "Office locations" },
                ].map((s, i) => (
                  <div key={i}>
                    <p className="text-[24px] text-slate-900 tracking-tight" style={{ fontWeight: 900 }}>{s.val}</p>
                    <p className="text-[12px] text-slate-500" style={{ fontWeight: 500 }}>{s.label}</p>
                  </div>
                ))}
              </motion.div>
            </div>

            {/* Right — photo stack */}
            <motion.div
              initial={{ opacity: 0, x: 32, scale: 0.97 }}
              animate={{ opacity: 1, x: 0, scale: 1 }}
              transition={{ duration: 0.75, delay: 0.15 }}
              className="hidden lg:block relative"
            >
              <div className="absolute inset-6 rounded-[32px] bg-gradient-to-br from-violet-400/20 via-pink-300/15 to-orange-400/20 blur-3xl" />
              <div className="relative rounded-[32px] overflow-hidden aspect-[4/5] max-h-[580px] shadow-2xl shadow-violet-900/20">
                {/* <ImageWithFallback */}
                <img
                  src="https://images.unsplash.com/photo-1758691737487-29b4fae83e95?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHx5b3VuZyUyMGRpdmVyc2UlMjB0ZWFtJTIwb2ZmaWNlJTIwY29sbGFib3JhdGlvbiUyMGVuZXJnZXRpY3xlbnwxfHx8fDE3Nzc2ODc3MTV8MA&ixlib=rb-4.1.0&q=80&w=1080"
                  alt="SCS Education team"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/50 via-slate-900/10 to-transparent" />

                {/* Slogan badge */}
                <div className="absolute bottom-5 left-5 right-5">
                  <div className="bg-white/95 backdrop-blur-sm rounded-2xl px-5 py-4 shadow-lg">
                    <p
                      className="text-[18px] text-slate-900 tracking-tight"
                      style={{ fontWeight: 900 }}
                    >
                      "Your Future,{" "}
                      <span
                        style={{
                          background: "linear-gradient(135deg, #7C3AED, #EC4899)",
                          WebkitBackgroundClip: "text",
                          WebkitTextFillColor: "transparent",
                        }}
                      >
                        We Care.
                      </span>
                      "
                    </p>
                  </div>
                </div>
              </div>

              {/* Floating card */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: 0.65 }}
                className="absolute -left-14 top-14 bg-white rounded-2xl px-5 py-4 shadow-2xl border border-slate-100"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-green-400 to-emerald-500 flex items-center justify-center text-[18px]">
                    🌟
                  </div>
                  <div>
                    <p className="text-[13px] text-slate-900" style={{ fontWeight: 700 }}>Actively Hiring</p>
                    <p className="text-[11px] text-slate-500" style={{ fontWeight: 400 }}>2 open roles right now</p>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ════════════════════════════════════════════════════════════════
          ABOUT SCS
      ════════════════════════════════════════════════════════════════ */}
      <section className="py-24 lg:py-32 bg-[#FAFAFA] relative overflow-hidden" id="about">
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-slate-200 to-transparent" />
        <div className="max-w-7xl mx-auto px-5 sm:px-8">

          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-slate-200 shadow-sm mb-6">
              <Building2 className="w-3.5 h-3.5 text-slate-500" />
              <span className="text-[12px] text-slate-600 uppercase tracking-widest" style={{ fontWeight: 700 }}>About SCS Education</span>
            </div>
            <h2 className="text-[38px] lg:text-[52px] text-slate-900 tracking-[-1.5px] mb-4" style={{ fontWeight: 900, lineHeight: 1.08 }}>
              We've been shaping{" "}
              <span
                style={{
                  background: "linear-gradient(135deg, #7C3AED, #F97316)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                futures since 2016.
              </span>
            </h2>
            <p className="text-[16px] text-slate-500 max-w-2xl mx-auto leading-relaxed" style={{ fontWeight: 400 }}>
              First established as 'Syncreative Consultancy Services', we are young, efficient and professional in education counseling — rebranded as <span className="text-slate-800" style={{ fontWeight: 600 }}>SCS Edu Group Sdn Bhd</span> in 2021 to reflect our continued growth.
            </p>
          </motion.div>

          {/* Story + Vision/Mission */}
          <div className="grid lg:grid-cols-2 gap-8 mb-10">
            {/* Story card */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="relative rounded-[28px] overflow-hidden bg-slate-950 p-8 lg:p-10"
            >
              <div className="absolute -top-20 -right-20 w-64 h-64 rounded-full bg-violet-600/15 blur-3xl pointer-events-none" />
              <div className="absolute -bottom-20 -left-20 w-64 h-64 rounded-full bg-orange-500/10 blur-3xl pointer-events-none" />
              <div
                className="absolute inset-0 pointer-events-none"
                style={{
                  backgroundImage: "radial-gradient(circle, rgba(255,255,255,0.03) 1px, transparent 1px)",
                  backgroundSize: "24px 24px",
                }}
              />
              <div className="relative z-10">
                <span className="text-[11px] text-slate-500 uppercase tracking-widest mb-4 block" style={{ fontWeight: 700 }}>Our Story</span>
                <p className="text-[16px] text-slate-200 leading-relaxed mb-6" style={{ fontWeight: 400 }}>
                  SCS Education was established with a clear mission: to assist every high school graduate find the right direction for further studies and create value in their career pathway.
                </p>
                <p className="text-[14px] text-slate-500 leading-relaxed" style={{ fontWeight: 400 }}>
                  Today, we represent more than <span className="text-slate-300" style={{ fontWeight: 600 }}>300 universities globally</span> across Malaysia, United Kingdom, Singapore, Australia, New Zealand, Canada, the USA and more.
                </p>
              </div>
            </motion.div>

            {/* Vision / Mission / Statement */}
            <div className="flex flex-col gap-4">
              {[
                {
                  icon: "🎯",
                  label: "Our Statement",
                  color: "from-violet-600 to-purple-600",
                  bg: "bg-violet-50",
                  border: "border-violet-100",
                  text: "To help every high school graduate choose the right direction for further studies and create real value in their career pathway.",
                },
                {
                  icon: "🔭",
                  label: "Our Vision",
                  color: "from-orange-500 to-amber-500",
                  bg: "bg-orange-50",
                  border: "border-orange-100",
                  text: "Become the #1 choice education platform for further studies in Malaysia.",
                },
                {
                  icon: "🚀",
                  label: "Our Mission",
                  color: "from-pink-500 to-rose-500",
                  bg: "bg-pink-50",
                  border: "border-pink-100",
                  text: "Provide high quality education services to fulfil each student's dream of further studies.",
                },
              ].map((item, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, x: 30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: i * 0.1 }}
                  className={`flex gap-5 items-start p-6 rounded-2xl ${item.bg} border ${item.border}`}
                >
                  <div className={`w-11 h-11 rounded-2xl bg-gradient-to-br ${item.color} flex items-center justify-center text-[20px] shadow-md flex-shrink-0`}>
                    {item.icon}
                  </div>
                  <div>
                    <p className="text-[11px] text-slate-500 uppercase tracking-widest mb-1.5" style={{ fontWeight: 700 }}>{item.label}</p>
                    <p className="text-[14px] text-slate-700 leading-relaxed" style={{ fontWeight: 500 }}>{item.text}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ════════════════════════════════════════════════════════════════
          WHY JOIN US
      ════════════════════════════════════════════════════════════════ */}
      <section className="py-24 lg:py-32 bg-white relative overflow-hidden" id="why">
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-slate-100 to-transparent" />
        <div className="absolute -top-24 right-0 w-[400px] h-[400px] rounded-full bg-violet-50/80 blur-3xl pointer-events-none" />
        <div className="absolute -bottom-24 left-0 w-[400px] h-[400px] rounded-full bg-orange-50/60 blur-3xl pointer-events-none" />

        <div className="max-w-7xl mx-auto px-5 sm:px-8 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-slate-50 border border-slate-200 mb-6">
              <Heart className="w-3.5 h-3.5 text-pink-500" />
              <span className="text-[12px] text-slate-600 uppercase tracking-widest" style={{ fontWeight: 700 }}>Why Join Us</span>
            </div>
            <h2 className="text-[38px] lg:text-[52px] text-slate-900 tracking-[-1.5px] mb-4" style={{ fontWeight: 900, lineHeight: 1.08 }}>
              5 reasons you'll{" "}
              <span
                style={{
                  background: "linear-gradient(135deg, #EC4899, #F97316)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                love it here.
              </span>
            </h2>
            <p className="text-[16px] text-slate-500 max-w-lg mx-auto leading-relaxed" style={{ fontWeight: 400 }}>
              We don't just offer a job. We offer a launch pad — with income, growth, and a team that genuinely cares about you.
            </p>
          </motion.div>

          {/* Cards: single unified responsive grid — 1 col mobile, 2 col sm, 3 col lg */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {WHY_JOIN.map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.09 }}
                className="bg-white rounded-3xl p-7 border border-slate-100 shadow-sm hover:shadow-2xl hover:shadow-slate-900/6 hover:-translate-y-1.5 transition-all duration-500 flex flex-col group"
              >
                <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${item.color} flex items-center justify-center text-[28px] shadow-lg ${item.shadow} mb-6 group-hover:scale-110 transition-transform duration-300`}>
                  {item.emoji}
                </div>
                <div className="mb-2">
                  <span className="text-[28px] text-slate-100 select-none float-right ml-2 leading-none" style={{ fontWeight: 900 }}>
                    0{i + 1}
                  </span>
                </div>
                <h3 className="text-[17px] text-slate-900 mb-3 leading-tight" style={{ fontWeight: 800 }}>{item.title}</h3>
                <p className="text-[13px] text-slate-500 leading-relaxed flex-1" style={{ fontWeight: 400 }}>{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ════════════════════════════════════════════════════════════════
          PERKS & CULTURE
      ════════════════════════════════════════════════════════════════ */}
      <section className="py-24 lg:py-32 bg-[#FAFAFA] overflow-hidden relative">
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-slate-200 to-transparent" />
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="grid lg:grid-cols-2 gap-16 lg:gap-20 items-center">

            {/* Left: photos */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.65 }}
              className="flex flex-col gap-4"
            >
              <div className="relative">
                <div className="absolute inset-6 rounded-[32px] bg-gradient-to-br from-orange-400/20 via-pink-300/15 to-violet-400/20 blur-3xl pointer-events-none" />
                <div className="grid grid-cols-2 gap-3 relative">
                  <div className="rounded-[24px] overflow-hidden aspect-[4/5] shadow-xl shadow-slate-900/10">
                    {/* <ImageWithFallback */}
                    <img
                      src="https://images.unsplash.com/photo-1681949270990-cecd4728d2e0?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxlZHVjYXRpb24lMjBjb25zdWx0YW50JTIwdGVhbSUyMG1lZXRpbmclMjB3aGl0ZWJvYXJkfGVufDF8fHx8MTc3NzY4NzcxNnww&ixlib=rb-4.1.0&q=80&w=1080"
                      alt="Team collaboration"
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="flex flex-col gap-3 pt-8">
                    <div className="rounded-[24px] overflow-hidden aspect-square shadow-xl shadow-slate-900/10">
                      {/* <ImageWithFallback */}
                      <img
                        src="https://images.unsplash.com/photo-1758691737584-a8f17fb34475?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHx0ZWFtJTIwY2VsZWJyYXRpb24lMjBvZmZpY2UlMjBwYXJ0eSUyMGFjaGlldmVtZW50fGVufDF8fHx8MTc3NzY4NzcxOHww&ixlib=rb-4.1.0&q=80&w=1080"
                        alt="Team celebration"
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="rounded-[24px] overflow-hidden aspect-video shadow-xl shadow-slate-900/10 flex-1">
                      {/* <ImageWithFallback */}
                      <img
                        src="https://images.unsplash.com/photo-1531547977107-a5f0f32d6d87?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBvZmZpY2UlMjB3b3Jrc3BhY2UlMjBNYWxheXNpYSUyMHByb2Zlc3Npb25hbHN8ZW58MXx8fHwxNzc3Njg3NzE2fDA&ixlib=rb-4.1.0&q=80&w=1080"
                        alt="Modern office"
                        className="w-full h-full object-cover"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Team badge — in-flow, below the photo grid, no overlap */}
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.4 }}
                className="self-center bg-white rounded-2xl px-5 py-3.5 shadow-xl border border-slate-100 flex items-center gap-3"
              >
                <div className="flex -space-x-2">
                  {["#7C3AED", "#F97316", "#06B6D4", "#EC4899"].map((c, i) => (
                    <div key={i} className="w-8 h-8 rounded-full border-2 border-white flex items-center justify-center text-[12px] text-white" style={{ backgroundColor: c, zIndex: 4 - i }}>
                      {["S", "A", "R", "+"].at(i)}
                    </div>
                  ))}
                </div>
                <div>
                  <p className="text-[13px] text-slate-900" style={{ fontWeight: 700 }}>Team of 20+ counselors</p>
                  <p className="text-[11px] text-slate-500" style={{ fontWeight: 400 }}>Across 3 offices in Malaysia</p>
                </div>
              </motion.div>
            </motion.div>

            {/* Right: perks list */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.65 }}
            >
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-slate-200 shadow-sm mb-6">
                <Star className="w-3.5 h-3.5 text-amber-400 fill-amber-400" />
                <span className="text-[12px] text-slate-600 uppercase tracking-widest" style={{ fontWeight: 700 }}>Perks & Culture</span>
              </div>

              <h2 className="text-[36px] lg:text-[46px] text-slate-900 tracking-[-1.5px] mb-5" style={{ fontWeight: 900, lineHeight: 1.1 }}>
                A place where you{" "}
                <span
                  style={{
                    background: "linear-gradient(135deg, #7C3AED, #06B6D4)",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                  }}
                >
                  actually thrive.
                </span>
              </h2>

              <p className="text-[15px] text-slate-500 leading-relaxed mb-10" style={{ fontWeight: 400 }}>
                We've built a culture where every team member is valued, heard, and empowered to grow — because when you win, we all win.
              </p>

              <div className="flex flex-col gap-4">
                {PERKS.map((perk, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, x: 20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4, delay: i * 0.07 }}
                    className="flex items-start gap-4"
                  >
                    <div className="w-9 h-9 rounded-2xl bg-gradient-to-br from-violet-600 to-purple-600 flex items-center justify-center text-white flex-shrink-0 shadow-md shadow-violet-500/20">
                      {perk.icon}
                    </div>
                    <div className="pt-2">
                      <p className="text-[14px] text-slate-700 leading-snug" style={{ fontWeight: 600 }}>{perk.label}</p>
                    </div>
                  </motion.div>
                ))}
              </div>

              <div className="mt-10">
                <button
                  onClick={() => openModal()}
                  className="inline-flex items-center gap-2 px-7 py-3.5 rounded-2xl bg-slate-900 text-white text-[14px] hover:bg-slate-800 hover:-translate-y-px transition-all shadow-lg cursor-pointer"
                  style={{ fontWeight: 700 }}
                >
                  I want to join <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ════════════════════════════════════════════════════════════════
          OPEN ROLES
      ════════════════════════════════════════════════════════════════ */}
      <section className="py-24 lg:py-32 bg-white relative overflow-hidden" id="roles">
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-slate-100 to-transparent" />
        <div className="max-w-7xl mx-auto px-5 sm:px-8">

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-14"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-slate-200 shadow-sm mb-6">
              <Briefcase className="w-3.5 h-3.5 text-slate-500" />
              <span className="text-[12px] text-slate-600 uppercase tracking-widest" style={{ fontWeight: 700 }}>Open Positions</span>
            </div>
            <h2 className="text-[38px] lg:text-[52px] text-slate-900 tracking-[-1.5px] mb-4" style={{ fontWeight: 900, lineHeight: 1.08 }}>
              We're hiring{" "}
              <span
                style={{
                  background: "linear-gradient(135deg, #7C3AED 0%, #EC4899 100%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                right now.
              </span>
            </h2>
            <p className="text-[16px] text-slate-500 max-w-lg mx-auto leading-relaxed" style={{ fontWeight: 400 }}>
              Explore our open roles below. No experience needed — just the drive to do meaningful work and grow fast.
            </p>
          </motion.div>

          <div className="max-w-3xl mx-auto flex flex-col gap-4">
            {OPEN_ROLES.map((role, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.12 }}
                className="group bg-white rounded-3xl border border-slate-100 shadow-sm hover:shadow-xl hover:shadow-slate-900/6 hover:-translate-y-1 transition-all duration-400 p-7 flex flex-col gap-5"
              >
                <div className="flex items-start gap-5 flex-1">
                  {/* Icon */}
                  <div className={`w-14 h-14 rounded-2xl flex items-center justify-center text-[26px] shadow-lg flex-shrink-0 ${
                    role.color === "violet"
                      ? "bg-gradient-to-br from-violet-600 to-purple-600 shadow-violet-500/25"
                      : "bg-gradient-to-br from-orange-500 to-pink-500 shadow-orange-500/25"
                  }`}>
                    🎓
                  </div>
                  <div>
                    <p className="text-[11px] text-slate-400 uppercase tracking-widest mb-1.5" style={{ fontWeight: 700 }}>
                      {role.dept}
                    </p>
                    <h3 className="text-[20px] text-slate-900 tracking-tight mb-2" style={{ fontWeight: 800 }}>
                      {role.title}
                    </h3>
                    <div className="flex flex-wrap gap-2">
                      {/* Type badge */}
                      <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-green-50 border border-green-100 text-[11px] text-green-700" style={{ fontWeight: 700 }}>
                        <Clock className="w-3 h-3" /> {role.type}
                      </span>
                      {/* Location badge */}
                      <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-violet-50 border border-violet-100 text-[11px] text-violet-700" style={{ fontWeight: 600 }}>
                        <MapPin className="w-3 h-3" /> {role.location}
                      </span>
                    </div>
                  </div>
                </div>

                <button
                  onClick={() => openModal(
                    `Sales Education Consultant (${role.location.split(",")[0]})`,
                    role.location
                  )}
                  className={`w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-3 rounded-2xl text-white text-[13px] shadow-lg transition-all hover:-translate-y-0.5 cursor-pointer ${
                    role.color === "violet"
                      ? "bg-gradient-to-r from-violet-600 to-purple-600 shadow-violet-500/25 hover:shadow-violet-500/40"
                      : "bg-gradient-to-r from-orange-500 to-pink-500 shadow-orange-500/25 hover:shadow-orange-500/40"
                  }`}
                  style={{ fontWeight: 700 }}
                >
                  Apply Now <ArrowRight className="w-3.5 h-3.5" />
                </button>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ════════════════════════════════════════════════════════════════
          LOCATIONS
      ════════════════════════════════════════════════════════════════ */}
      <section className="py-24 lg:py-32 bg-[#FAFAFA] relative overflow-hidden" id="locations">
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-slate-200 to-transparent" />
        <div className="max-w-7xl mx-auto px-5 sm:px-8">

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-14"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-slate-200 shadow-sm mb-6">
              <MapPin className="w-3.5 h-3.5 text-slate-500" />
              <span className="text-[12px] text-slate-600 uppercase tracking-widest" style={{ fontWeight: 700 }}>Where We Are</span>
            </div>
            <h2 className="text-[38px] lg:text-[52px] text-slate-900 tracking-[-1.5px] mb-4" style={{ fontWeight: 900, lineHeight: 1.08 }}>
              3 offices.{" "}
              <span
                style={{
                  background: "linear-gradient(135deg, #F97316, #EC4899)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                All across Malaysia.
              </span>
            </h2>
            <p className="text-[16px] text-slate-500 max-w-lg mx-auto leading-relaxed" style={{ fontWeight: 400 }}>
              From Kuantan to Shah Alam to Kepong — our offices are designed for collaboration, creativity, and growth.
            </p>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {OFFICES.map((office, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 28 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                className="group bg-white rounded-3xl border border-slate-100 shadow-sm hover:shadow-2xl hover:shadow-slate-900/6 hover:-translate-y-1.5 transition-all duration-500 overflow-hidden"
              >
                {/* Colour top band */}
                <div className={`h-2 bg-gradient-to-r ${office.color}`} />
                <div className="p-7">
                  <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${office.color} flex items-center justify-center text-[26px] shadow-lg mb-5 group-hover:scale-105 transition-transform duration-300`}>
                    {office.emoji}
                  </div>
                  <div className="mb-1">
                    <span className="text-[11px] text-slate-400 uppercase tracking-widest" style={{ fontWeight: 700 }}>
                      {office.name}
                    </span>
                  </div>
                  <h3 className="text-[22px] text-slate-900 tracking-tight mb-1" style={{ fontWeight: 900 }}>
                    {office.city}
                  </h3>
                  <p className="text-[13px] text-slate-500 mb-5" style={{ fontWeight: 500 }}>
                    {office.state}
                  </p>
                  <div className="flex gap-2.5 items-start">
                    <MapPin className="w-4 h-4 text-slate-400 flex-shrink-0 mt-0.5" />
                    <p className="text-[13px] text-slate-500 leading-relaxed" style={{ fontWeight: 400 }}>
                      {office.address}
                    </p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ════════════════════════════════════════════════════════════════
          CTA APPLY SECTION
      ════════════════════════════════════════════════════════════════ */}
      <section className="py-24 bg-white relative overflow-hidden" id="apply">
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-slate-100 to-transparent" />
        <div className="max-w-4xl mx-auto px-5 sm:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.65 }}
            className="relative rounded-[32px] overflow-hidden bg-slate-950 p-12 lg:p-16"
          >
            {/* BG glows */}
            <div className="absolute -top-28 -left-28 w-72 h-72 rounded-full bg-violet-600/20 blur-3xl pointer-events-none" />
            <div className="absolute -bottom-28 -right-28 w-72 h-72 rounded-full bg-orange-500/15 blur-3xl pointer-events-none" />
            <div
              className="absolute inset-0 pointer-events-none"
              style={{
                backgroundImage: "radial-gradient(circle, rgba(255,255,255,0.035) 1px, transparent 1px)",
                backgroundSize: "24px 24px",
              }}
            />

            <div className="relative z-10">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 border border-white/10 mb-8">
                <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
                <span className="text-[12px] text-slate-300 uppercase tracking-widest" style={{ fontWeight: 700 }}>
                  Now Hiring
                </span>
              </div>

              <h2 className="text-[32px] lg:text-[46px] text-white tracking-[-1.5px] mb-5" style={{ fontWeight: 900, lineHeight: 1.1 }}>
                Ready to start your{" "}
                <span
                  style={{
                    background: "linear-gradient(90deg, #A78BFA, #F9A8D4, #FDBA74)",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                  }}
                >
                  journey with us?
                </span>
              </h2>
              <p className="text-[15px] text-slate-400 mb-10 max-w-xl mx-auto leading-relaxed" style={{ fontWeight: 400 }}>
                Take the first step. Send us your application — it takes less than 3 minutes. Our HR team will reach out within 2 business days.
              </p>

              <button
                onClick={() => openModal()}
                className="inline-flex items-center gap-3 px-10 py-4 rounded-2xl bg-gradient-to-r from-violet-500 via-purple-500 to-pink-500 text-white text-[16px] shadow-2xl shadow-violet-500/30 hover:shadow-violet-500/50 hover:-translate-y-0.5 transition-all cursor-pointer"
                style={{ fontWeight: 700 }}
              >
                Apply Now — It's Free <ArrowRight className="w-5 h-5" />
              </button>

              <div className="mt-8 flex flex-wrap items-center justify-center gap-x-8 gap-y-3">
                {[
                  "No experience required",
                  "Response within 2 days",
                  "3 office locations",
                ].map((t) => (
                  <div key={t} className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-violet-400" />
                    <span className="text-[13px] text-slate-400" style={{ fontWeight: 500 }}>{t}</span>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ════════════════════════════════════════════════════════════════
          FOOTER (simple)
      ════════════════════════════════════════════════════════════════ */}
      <footer className="bg-slate-950 py-10 border-t border-slate-800/60" style={{ fontFamily: "Inter, sans-serif" }}>
        <div className="max-w-7xl mx-auto px-5 sm:px-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <a href="/" className="flex items-center gap-2.5">
          <img src={logo} alt="" className="w-10 h-10"/>
            <span className="text-[15px] text-white tracking-tight" style={{ fontWeight: 800 }}>
              SCS <span className="text-amber-500">Education</span>
            </span>
          </a>
          <p className="text-[12px] text-slate-600" style={{ fontWeight: 400 }}>
            © 2026 SCS Edu Group Sdn Bhd · All rights reserved.
          </p>
          <div className="flex items-center gap-4">
            <a href="/" className="text-[12px] text-slate-600 hover:text-slate-400 transition-colors" style={{ fontWeight: 400 }}>Back to Home</a>
            <a href="#apply" className="text-[12px] text-violet-500 hover:text-violet-400 transition-colors" style={{ fontWeight: 600 }}>Apply Now →</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
